import React, { useCallback } from 'react';
import useRootSelector from 'common/hooks/useRootSelector';
import { useLocation, useNavigate } from 'react-router-dom';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { FormattedMessage } from 'react-intl';
import {
  Box,
  Button,
  createStyles,
  FormControl,
  InputLabel,
  makeStyles,
  MenuItem,
  Select,
  Theme,
} from '@material-ui/core';
import { Add as AddIcon } from '@material-ui/icons';
import HeightIcon from '@material-ui/icons/Height';
import { ReactComponent as Delete } from 'img/icons/x-bold_deprecated.svg';
import { FormContainer, TextField } from 'common/components';
import { IScriptDetailed, IScriptDetailedImt } from 'modules/crm/interfaces/scripts';
import { selectQueryPageList } from 'common/state/queryPage-lists/selectors';
import { QueryPageList, ActiveInactiveStatus } from 'common/constants';
import inputLabels from 'common/messages/inputLabels';
import messages from 'common/messages/messages';
import isFormDirty from 'common/hooks/isFormDirty';
import DiscardChangesModalProvider from 'common/modals/DiscardChangesModal/DiscardChangesModalProvider';
import IntlService from 'services/intl/IntlService';
import { getRequiredErrorMessage } from 'common/utils/validation';
import { useRenderIntlMessage } from 'common/hooks/useRenderIntlMessage';
import scriptMessages from 'modules/crm/messages/scripts';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    stepWrapper: {
      backgroundColor: theme.palette.background.default,
      padding: theme.spacing(2, 2, 2, 1.5),
      marginBottom: theme.spacing(1.5),
      borderRadius: 4,
    },
    selectWrapper: {
      width: 102,
      [theme.breakpoints.down('xs')]: {
        width: '100%',
      },
    },
    inputContainer: {
      [theme.breakpoints.down('xs')]: {
        flexDirection: 'column',
      },
    },
    inputWrapper: {
      flex: 1,
      [theme.breakpoints.down('xs')]: {
        marginRight: 0,
        marginBottom: theme.spacing(1.5),
      },
    },
    moveStepWrapper: {
      //
    },
    stepButton: {
      width: 18,
      height: 18,
      color: theme.palette.text.primary,
      opacity: 0.4,
      padding: 0,
      minWidth: 0,
      '& svg': {
        width: 20,
      },
    },
    newStepButton: {
      backgroundColor: theme.palette.background.default,
      width: '100%',
    },
  }),
);

interface IProps {
  title: JSX.Element;
  script?: IScriptDetailedImt | null;
  isLoading: boolean;
  onSubmit: (formData: IScriptDetailed) => void;
}

const validationSchema = yup.object().shape({
  title: yup
    .string()
    .trim()
    .matches(/^.{0,100}$/, () => scriptMessages.scriptNameLength)
    .required(getRequiredErrorMessage),
  scriptStepDtoList: yup.array().of(
    yup.object().shape({
      title: yup
        .string()
        .trim()
        .matches(/^.{0,100}$/, () => scriptMessages.scriptStepNameLength)
        .required(getRequiredErrorMessage),
      instruction: yup
        .string()
        .matches(/^(?=[\S\s]{0,5000}$)[\S\s]*/, () => scriptMessages.scriptInstructionLength)
        .required(getRequiredErrorMessage),
    }),
  ),
});

const defaultFormValues = {
  title: '',
  active: true,
  scriptStepDtoList: [{ title: '', allowInput: true, instruction: '' }],
};

export default function ScriptForm({ title, script, isLoading, onSubmit }: IProps): JSX.Element {
  const classes = useStyles();
  const queryScripts: string = useRootSelector(selectQueryPageList(QueryPageList.SCRIPTS));
  const renderIntlMessage = useRenderIntlMessage();

  const initFormValues = () => {
    if (!script) {
      return defaultFormValues;
    }

    const scriptData = script.toJS();

    const sortedScriptSteps = scriptData?.scriptStepDtoList.sort(
      (firstStep, secondStep) => firstStep.index - secondStep.index,
    );

    return {
      ...defaultFormValues,
      ...script.toJS(),
      scriptStepDtoList: sortedScriptSteps,
    };
  };

  const { register, handleSubmit, control, formState } = useForm<IScriptDetailed>({
    defaultValues: {
      ...initFormValues(),
    },
    resolver: yupResolver(validationSchema) as any, // TODO - PRM-1810 need resolver type
    mode: 'onBlur',
  });

  const { errors } = formState;
  const regTitle = register('title');

  const { fields, append, remove, move } = useFieldArray({
    control,
    name: 'scriptStepDtoList',
  });

  const handleRemove = index => {
    if (fields.length > 1) remove(index);
  };

  const navigate = useNavigate();
  const location = useLocation();
  const isDirty = isFormDirty(formState);

  const handleFormSubmit = values => {
    onSubmit({
      ...values,
      scriptStepDtoList: values.scriptStepDtoList.map((item, index) => ({
        ...item,
        index,
      })),
    });
  };

  const handleCancelButton = useCallback(() => {
    navigate(`/${location.pathname.split('/')[1]}/scripts${queryScripts}`);
  }, [location.pathname, navigate, queryScripts]);

  const handleDrag = ({ source, destination }) => {
    if (destination) {
      move(source.index, destination.index);
    }
  };

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} style={{ height: '100%' }}>
      <FormContainer
        title={title}
        maxWidth={625}
        isSubmitting={isLoading}
        onCancel={handleCancelButton}
      >
        <Box display="flex" mb={1.5} className={classes.inputContainer}>
          <Box mr={1.5} className={classes.inputWrapper}>
            <Controller
              control={control}
              name="title"
              render={({ field }) => (
                <TextField
                  {...regTitle}
                  value={field.value}
                  defaultValue={defaultFormValues.title}
                  variant="outlined"
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  label={IntlService.formatMessage(inputLabels.name)}
                  fullWidth
                  error={!!errors.title}
                  helperText={renderIntlMessage(errors?.title?.message)}
                />
              )}
            />
          </Box>
          <Box className={classes.selectWrapper}>
            <FormControl fullWidth variant="outlined">
              <InputLabel id="status">
                <FormattedMessage {...inputLabels.status} />
              </InputLabel>

              <Controller
                control={control}
                name="active"
                render={({ field }) => (
                  <Select
                    value={field.value ? 1 : 0}
                    name={field.name}
                    defaultValue={defaultFormValues.active}
                    onChange={e => {
                      field.onChange(!!e.target.value);
                    }}
                    labelId="status"
                    variant="outlined"
                    fullWidth
                  >
                    {ActiveInactiveStatus.map(status => (
                      <MenuItem value={Number(status.value)} key={status.key}>
                        {status.label}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />
            </FormControl>
          </Box>
        </Box>
        <DragDropContext onDragEnd={handleDrag}>
          <Box>
            <Droppable droppableId="test-items">
              {provided => (
                <div {...provided.droppableProps} ref={provided.innerRef}>
                  {fields.map((item, index) => {
                    const regStepTitle = register(`scriptStepDtoList.${index}.title`);
                    const regStepInstruction = register(`scriptStepDtoList.${index}.instruction`);

                    return (
                      // eslint-disable-next-line react/no-array-index-key
                      <Draggable key={`test.${index}`} draggableId={`item-${index}`} index={index}>
                        {draggableProvided => (
                          <Box
                            key={item.id}
                            ref={draggableProvided.innerRef}
                            {...draggableProvided.draggableProps}
                          >
                            <Box className={classes.stepWrapper} display="flex" key={item.id}>
                              <Box className={classes.moveStepWrapper} pt={1.5} mr={1.5}>
                                <div {...draggableProvided.dragHandleProps}>
                                  <Button className={classes.stepButton}>
                                    <HeightIcon />
                                  </Button>
                                </div>
                              </Box>
                              <Box flex={1}>
                                <Box display="flex" flexDirection="column">
                                  <Box display="flex" mb={1.5} className={classes.inputContainer}>
                                    <Box mr={1.5} className={classes.inputWrapper}>
                                      <Controller
                                        control={control}
                                        name={`scriptStepDtoList.${index}.title`}
                                        render={({ field }) => (
                                          <TextField
                                            {...regStepTitle}
                                            value={field.value}
                                            onChange={field.onChange}
                                            onBlur={field.onBlur}
                                            defaultValue={item.title}
                                            variant="outlined"
                                            label={IntlService.formatMessage(inputLabels.stepName)}
                                            error={!!errors?.scriptStepDtoList?.[index]?.title}
                                            helperText={renderIntlMessage(
                                              errors.scriptStepDtoList?.[index]?.title?.message,
                                            )}
                                            fullWidth
                                          />
                                        )}
                                      />
                                    </Box>
                                    <Box className={classes.selectWrapper}>
                                      <FormControl fullWidth variant="outlined">
                                        <InputLabel
                                          id={`scriptStepDtoList.${index}.allowsLabel`}
                                          style={{ zIndex: 1 }}
                                        >
                                          <FormattedMessage {...inputLabels.allowsInput} />
                                        </InputLabel>

                                        <Controller
                                          control={control}
                                          name={`scriptStepDtoList.${index}.allowInput`}
                                          defaultValue={item.allowInput}
                                          render={({ field }) => (
                                            <Select
                                              defaultValue={item.allowInput}
                                              value={field.value ? 1 : 0}
                                              name={field.name}
                                              onChange={e => {
                                                field.onChange(!!e.target.value);
                                              }}
                                              labelId={`scriptStepDtoList.${index}.allowsLabel`}
                                              variant="outlined"
                                              fullWidth
                                            >
                                              <MenuItem value={1}>Yes</MenuItem>
                                              <MenuItem value={0}>No</MenuItem>
                                            </Select>
                                          )}
                                        />
                                      </FormControl>
                                    </Box>
                                  </Box>
                                  <Box>
                                    <Controller
                                      control={control}
                                      name={`scriptStepDtoList.${index}.instruction`}
                                      render={({ field }) => (
                                        <TextField
                                          {...regStepInstruction}
                                          value={field.value}
                                          variant="outlined"
                                          label={IntlService.formatMessage(
                                            inputLabels.instructions,
                                          )}
                                          inputProps={{ maxLength: 5001 }}
                                          fullWidth
                                          multiline
                                          onChange={field.onChange}
                                          onBlur={field.onBlur}
                                          minRows={2}
                                          defaultValue={item.instruction}
                                          error={!!errors?.scriptStepDtoList?.[index]?.instruction}
                                          helperText={renderIntlMessage(
                                            errors?.scriptStepDtoList?.[index]?.instruction
                                              ?.message,
                                          )}
                                        />
                                      )}
                                    />
                                  </Box>
                                </Box>
                              </Box>
                              <Box ml={1.75} pt={1.5}>
                                <Button
                                  className={classes.stepButton}
                                  onClick={() => handleRemove(index)}
                                >
                                  <Delete />
                                </Button>
                              </Box>
                            </Box>
                          </Box>
                        )}
                      </Draggable>
                    );
                  })}

                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </Box>
        </DragDropContext>
        <Box>
          <Button
            color="primary"
            onClick={() => {
              append({
                title: '',
                allowInput: true,
                instruction: '',
              } as any); // TODO - PRM-1810 need type
            }}
            startIcon={<AddIcon />}
            className={classes.newStepButton}
          >
            <FormattedMessage {...messages.newStepBtn} />
          </Button>
        </Box>
      </FormContainer>

      {isDirty ? <DiscardChangesModalProvider headerIds routeItemIds breadcrumbIds /> : null}
    </form>
  );
}
